// Topic bucketing for the /articles hub. Articles carry free-form frontmatter
// tags in the `metadata` jsonb blob; the index page groups them into a small
// set of reader-intent topics instead of exposing a raw tag cloud.

export type ArticleTopicKey =
    | "craft"
    | "progression"
    | "worldbuilding"
    | "business"
    | "guides";

export interface ArticleTopic {
    key: ArticleTopicKey;
    /** Short label used on filter chips and card badges. */
    label: string;
    /** Intent-phrased section subtitle ("what the reader wants"). */
    intent: string;
}

export const ARTICLE_TOPICS: ArticleTopic[] = [
    {
        key: "craft",
        label: "Writing Craft",
        intent: "Write chapters readers finish",
    },
    {
        key: "progression",
        label: "LitRPG & Progression",
        intent: "Power systems that hold up",
    },
    {
        key: "worldbuilding",
        label: "Worldbuilding",
        intent: "Build worlds readers believe",
    },
    {
        key: "business",
        label: "Earnings & Platforms",
        intent: "Get paid for your fiction",
    },
    {
        key: "guides",
        label: "IlorisNovel Guides",
        intent: "How the platform works",
    },
];

export function getArticleTopic(key: ArticleTopicKey): ArticleTopic {
    return ARTICLE_TOPICS.find((t) => t.key === key) ?? ARTICLE_TOPICS[0];
}

export function isArticleTopicKey(value: string): value is ArticleTopicKey {
    return ARTICLE_TOPICS.some((t) => t.key === value);
}

// Normalized-tag keyword sets per bucket. An article's topic is the bucket
// with the most tag matches; ties resolve in this priority order.
const TOPIC_KEYWORDS: Record<Exclude<ArticleTopicKey, "guides">, Set<string>> =
    {
        business: new Set([
            "monetization",
            "patreon",
            "fees",
            "platform fees",
            "author business",
            "publishing",
            "publishing platforms",
            "platforms",
            "substack",
            "wattpad",
            "migration",
            "kindle unlimited",
            "comparison",
            "marketing",
            "covers",
            "ai art",
            "scribble hub",
            "web serial",
            "royal road",
        ]),
        progression: new Set([
            "litrpg",
            "progression fantasy",
            "cultivation",
            "power systems",
            "power scaling",
            "stat blocks",
            "system messages",
            "weak to strong",
            "tropes",
        ]),
        worldbuilding: new Set([
            "worldbuilding",
            "world building",
            "magic systems",
            "castles",
            "currency",
            "elves",
            "stereotypes",
        ]),
        craft: new Set([
            "craft",
            "writing craft",
            "writing advice",
            "writing process",
            "character",
            "description",
            "character description",
            "character development",
            "openings",
            "novel openings",
            "pacing",
            "story structure",
            "interiority",
            "chapter titles",
            "novel titles",
            "novel writing",
            "show dont tell",
            "romance",
            "subplot",
            "first draft",
            "mid novel slump",
            "discovery writing",
            "pantser vs plotter",
            "serial writing",
            "fiction writing",
            "stakes",
            "protagonist",
            "fantasy writing",
            "audiobook",
            "dialogue",
        ]),
    };

const TIE_PRIORITY: Exclude<ArticleTopicKey, "guides">[] = [
    "business",
    "progression",
    "worldbuilding",
    "craft",
];

function normalizeTag(tag: string): string {
    return tag.toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Derive the display topic for an article from its frontmatter metadata.
 * Untagged articles are the hand-written platform guides.
 */
export function deriveArticleTopic(metadata: unknown): ArticleTopicKey {
    if (!metadata || typeof metadata !== "object") return "guides";
    const raw = (metadata as Record<string, unknown>).tags;
    const tags = Array.isArray(raw)
        ? raw.filter((t): t is string => typeof t === "string").map(normalizeTag)
        : [];
    if (tags.length === 0) return "guides";

    let best: Exclude<ArticleTopicKey, "guides"> = "craft";
    let bestScore = 0;
    for (const key of TIE_PRIORITY) {
        const score = tags.filter((t) => TOPIC_KEYWORDS[key].has(t)).length;
        if (score > bestScore) {
            best = key;
            bestScore = score;
        }
    }
    return best;
}

/** Estimated read time from raw markdown length (~6 chars/word, 225 wpm). */
export function estimateReadMinutes(markdownChars: number): number {
    const words = markdownChars / 6;
    return Math.max(2, Math.round(words / 225));
}
